import type { FormEvent } from 'react';
import { useState } from 'react';
import type { Nullable } from 'corgi-types';
import type { MutationOptInToHomeClubArgs } from 'graphql-ts-types';

import { executeQuery } from '../../gql';
import type { OptOutOfHomeClubMutation } from '../../gql/generated/schema';
import optInToHomeClubMutation from '../../gql/mutation/homeclub/opt-in-home-club.gql';
import optOutOfHomeClubMutation from '../../gql/mutation/homeclub/opt-out-home-club.gql';
import { useLocalization } from '../../providers/LocaleProvider';
import {
  dispatchFailureTrackingEvent,
  dispatchSuccessTrackingEvents,
} from '../../tracking/home-club';
import type { GraphQLExtensions } from '../../types/graphql';
import type { DispatchState } from '../../types/React';
import { isErrorWithExtensions } from '../../utils/error-handlers/error-with-extensions';
import { extractErrorMessage } from '../../utils/error-handlers/gql-extensions';

interface MutationOptInToHomeClubResponse {
  optInToHomeClub: string;
}

interface UseFormSubmitOptions {
  setErrorText: DispatchState<Nullable<string>>;
  setSuccessText?: DispatchState<Nullable<string>>;
  onSuccess: (homeClubId: string) => void;
  tracking: {
    eventName?: string;
    buttonLabel: string;
    title?: string;
  };
  withSuccessDelay?: boolean;
}

const performOptInToHomeClubMutation = (variables = {}) =>
  executeQuery<MutationOptInToHomeClubResponse, Partial<MutationOptInToHomeClubArgs>>({
    variables,
    query: optInToHomeClubMutation,
    useApq: true,
  });

export const performOptOutOfHomeClubMutation = () =>
  executeQuery<OptOutOfHomeClubMutation>({
    query: optOutOfHomeClubMutation,
    useApq: true,
  });

export const usePerformOptInToHomeClubHandler = ({
  tracking,
  setErrorText,
  setSuccessText,
}: UseFormSubmitOptions) => {
  const { t } = useLocalization();

  return async () => {
    setErrorText('');

    try {
      /**
       * https://home24.atlassian.net/browse/RUN-3514 introduced
       * the 'subscribeButlersNewsletter' param as always false.
       */
      const { optInToHomeClub } = await performOptInToHomeClubMutation({
        subscribeButlersNewsletter: false,
      });

      dispatchSuccessTrackingEvents(tracking);

      if (setSuccessText) {
        setSuccessText(t('h24_hc_joined_success_message'));
      }

      return optInToHomeClub;
    } catch (error) {
      const extensions: GraphQLExtensions = isErrorWithExtensions(error) ? error.extensions : {};
      const message = extractErrorMessage(extensions, t);

      dispatchFailureTrackingEvent(tracking);

      setErrorText(message);
    }
  };
};

export const useFormSubmit = (options: UseFormSubmitOptions) => {
  const [isLoading, setIsLoading] = useState(false);
  const optInToHomeClubHandler = usePerformOptInToHomeClubHandler(options);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    setIsLoading(true);
    const newHomeClubId = await optInToHomeClubHandler();
    setIsLoading(false);

    if (options.withSuccessDelay) {
      // onSuccess leads to birthday card being shown
      // The birthday card should be shown after the homeCLubNewsletter subscription is updated
      // So, we wait for subscription to be updated
      await new Promise(resolve => setTimeout(resolve, 500));
    }

    if (newHomeClubId) {
      options.onSuccess(newHomeClubId);
    }
  };

  return {
    isLoading,
    handleSubmit,
  };
};
